import React, { useMemo } from 'react';
import { PrimaryButton } from '../ui/Primitives.jsx';
import { fillTemplate } from '../../lib/helpers.js';
import { inferPlanSettingsFromAnswers } from '../../config/aiPlanAdvisor.js';
import { generateWorkoutPlan } from '../../config/planGenerator.js';
import { getExercisesBySlugs } from '../../config/exercisesConfig.js';
import { computeCaloriesBurned } from '../../lib/calories.js';

// ============================================================================
// "SEU PLANO ESTÁ PRONTO" — duplicado em 16/09/2026 direto de
// fitclutch-mobile/src/components/onboarding/PlanPreviewScreen.js (pedido
// da Gladis, comparando com o Befit: mostrar o treino de verdade que a
// pessoa vai fazer, não um mockup genérico). Usa o MESMO motor real de
// geração de plano do app mobile (aiPlanAdvisor.js + planGenerator.js,
// duplicados aqui) — os exercícios do Dia 1 são calculados a partir das
// respostas reais dela (nível, objetivo, local, dias por semana, lesões,
// foco muscular), igual no app.
// ============================================================================
export default function PlanPreviewScreen({ step, answers, gender, onNext }) {
  const planData = useMemo(() => {
    const inferred = inferPlanSettingsFromAnswers(answers);
    if (!inferred) return null;
    const plan = generateWorkoutPlan(inferred.settings);
    const day1 = plan.days?.[0];
    if (!day1 || !day1.slugs?.length) return null;
    const exercises = getExercisesBySlugs(day1.slugs, gender);

    const allMinutes = plan.days.map((d) => d.estimatedMinutes).filter((n) => n > 0);
    const minMinutes = Math.min(...allMinutes);
    const maxMinutes = Math.max(...allMinutes);
    const durationText = minMinutes === maxMinutes ? `${minMinutes}` : `${minMinutes}-${maxMinutes}`;

    const calories = computeCaloriesBurned({
      durationSeconds: day1.estimatedMinutes * 60,
      weightKg: answers.weight_current,
    });

    return {
      totalWorkouts: plan.days.length,
      durationText,
      calories,
      exercises,
    };
  }, [answers, gender]);

  const headlineParts = useMemo(() => splitHighlight(step.headline, step.highlightWord), [step.headline, step.highlightWord]);

  return (
    <div className="phone-frame flex flex-col safe-top safe-bottom">
      <div className="flex-1 overflow-y-auto px-5 pt-8 pb-6">
        <h1 className="font-display text-2xl font-extrabold text-center leading-snug mb-7">
          {headlineParts.map((p, i) =>
            p.highlight ? (
              <span key={i} className="text-cyan">{p.text}</span>
            ) : (
              <span key={i}>{p.text}</span>
            )
          )}
        </h1>

        {planData ? (
          <>
            <div className="flex items-center bg-surface rounded-xl2 py-4 mb-7">
              <StatCol value={String(planData.totalWorkouts)} label={step.statsLabels.workouts} />
              <div className="w-px h-7 bg-surface-2" />
              <StatCol value={`${planData.durationText} min`} label={step.statsLabels.duration} />
              <div className="w-px h-7 bg-surface-2" />
              <StatCol value={String(planData.calories)} label={step.statsLabels.calories} />
            </div>

            <p className="text-xs font-extrabold tracking-wide text-ink-muted mb-3">
              {fillTemplate(step.dayLabelTemplate, { n: '1' })}
            </p>

            <div className="flex flex-col gap-4">
              {planData.exercises.map((ex) => (
                <div key={ex.slug} className="rounded-xl2 overflow-hidden bg-surface">
                  <div className="h-36 bg-white">
                    {ex.video && (
                      <video
                        src={ex.video}
                        className="w-full h-full object-contain"
                        autoPlay
                        loop
                        muted
                        playsInline
                      />
                    )}
                  </div>
                  <div className="p-3.5">
                    <p className="font-semibold text-sm mb-1">{ex.name}</p>
                    <p className="text-ink-muted text-xs">
                      {fillTemplate(step.setsRepsTemplate, { sets: '3', reps: String(ex.targetReps) })}
                      {' • '}
                      {ex.isBodyweight ? step.bodyweightLabel : `${ex.defaultWeightKg} kg`}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : (
          <p className="text-center text-ink-muted text-sm">Montando seu plano...</p>
        )}
      </div>

      <div className="px-5 py-4">
        <PrimaryButton onClick={onNext}>{step.cta}</PrimaryButton>
      </div>
    </div>
  );
}

function splitHighlight(text, word) {
  if (!text || !word) return [{ text: text ?? '' }];
  const idx = text.indexOf(word);
  if (idx === -1) return [{ text }];
  return [
    { text: text.slice(0, idx) },
    { text: word, highlight: true },
    { text: text.slice(idx + word.length) },
  ];
}

function StatCol({ value, label }) {
  return (
    <div className="flex-1 flex flex-col items-center">
      <span className="font-display text-lg font-extrabold">{value}</span>
      <span className="text-[11px] text-ink-muted mt-1">{label}</span>
    </div>
  );
}
